import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MontblancCalendar } from './MontblancCalendar';
import { MontblancNotes } from '../journal/MontblancNotes';
import { ChevronLeft, ChevronRight, Plus, Clock, MapPin } from 'lucide-react';
import { format, isToday, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Event {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  category: 'work' | 'health' | 'personal' | 'inspiration' | 'reflection';
  description?: string;
  mood?: 'morning' | 'evening' | 'inspiration';
}

interface ScheduleProps {
  events?: Event[];
}

const mockEvents: Event[] = [
  {
    id: '1',
    title: 'Meditação Matinal',
    startTime: '07:00',
    endTime: '07:30',
    category: 'reflection',
    mood: 'morning',
    description: 'Momento de conexão e preparação para o dia'
  },
  {
    id: '2',
    title: 'Escrita Criativa',
    startTime: '08:00',
    endTime: '09:00',
    category: 'inspiration',
    mood: 'inspiration',
    description: 'Explorando ideias e desenvolvendo narrativas'
  },
  {
    id: '3',
    title: 'Reunião de Estratégia',
    startTime: '10:00',
    endTime: '11:00',
    category: 'work',
    description: 'Planejamento trimestral com a equipe'
  },
  {
    id: '4',
    title: 'Yoga & Mindfulness',
    startTime: '17:00',
    endTime: '18:00',
    category: 'health',
    mood: 'evening',
    description: 'Prática de yoga para equilibrar corpo e mente'
  }
];

const weekDays = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const hours = Array.from({ length: 24 }, (_, i) => i);

const getEventColor = (type: string) => {
  switch (type) {
    case 'work':
      return 'bg-[rgb(88,86,214,0.2)] border-[rgb(88,86,214,0.4)] text-[rgb(88,86,214)]';
    case 'health':
      return 'bg-[rgb(0,122,255,0.2)] border-[rgb(0,122,255,0.4)] text-[rgb(0,122,255)]';
    case 'personal':
      return 'bg-[rgb(52,199,89,0.2)] border-[rgb(52,199,89,0.4)] text-[rgb(52,199,89)]';
    case 'inspiration':
      return 'bg-[rgb(255,149,0,0.2)] border-[rgb(255,149,0,0.4)] text-[rgb(255,149,0)]';
    case 'reflection':
      return 'bg-[rgb(88,86,214,0.2)] border-[rgb(88,86,214,0.4)] text-[rgb(88,86,214)]';
    default:
      return 'bg-white/20 border-white/40 text-white';
  }
};

export const Schedule: React.FC<ScheduleProps> = ({ events = mockEvents }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());

  const handleAddEvent = (date: Date) => {
    console.log('Adicionar evento para:', date);
  };

  const onPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const onNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const onNewEvent = () => {
    // Implementar lógica de criar novo evento
    console.log('Criar novo evento');
  };

  const onEventClick = (event: Event) => {
    // Implementar lógica de clicar em evento
    console.log('Clicar em evento:', event);
  };

  // Gerar array de datas para a semana atual
  const getWeekDays = () => {
    const days = [];
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - start.getDay() + 1); // Começar na segunda-feira

    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-[28px] font-semibold text-white">Agenda</h2>
        <div className="flex items-center gap-2">
          <button 
            onClick={onPreviousDay}
            className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl p-2 transition-all duration-200"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button 
            onClick={() => setSelectedDate(new Date())}
            className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl py-2 px-3 text-sm font-medium transition-all duration-200"
          >
            Hoje
          </button>
          <button 
            onClick={onNextDay}
            className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl p-2 transition-all duration-200"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button 
            onClick={onNewEvent}
            className="bg-white hover:bg-white/90 text-black rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Novo Evento
          </button>
        </div>
      </div>

      {/* Calendário Montblanc */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[rgb(28,28,30)] rounded-2xl p-6"
      >
        <MontblancCalendar 
          events={events}
          onAddEvent={handleAddEvent}
        />
      </motion.div>

      {/* Notas Montblanc */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-[rgb(28,28,30)] rounded-2xl p-6"
      >
        <MontblancNotes />
      </motion.div>

      {/* Timeline */}
      <div className="bg-[rgb(28,28,30)] rounded-2xl p-6">
        <div className="relative">
          {hours.map((hour) => (
            <div
              key={hour}
              className="flex items-center h-20 border-t border-[rgb(44,44,46)]"
            >
              <div className="w-16 -mt-3 text-sm text-[rgb(152,152,157)]">
                {hour.toString().padStart(2, '0')}:00
              </div>
              <div className="flex-1 relative">
                {events
                  .filter((event) => {
                    const eventHour = parseInt(event.startTime.split(':')[0]);
                    return eventHour === hour;
                  })
                  .map((event) => (
                    <div
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      className={`
                        absolute w-[calc(100%-1rem)] p-4 rounded-2xl cursor-pointer
                        transition-all duration-200 hover:scale-[1.02]
                        ${getEventColor(event.category)}
                      `}
                      style={{
                        top: `${(parseInt(event.startTime.split(':')[1]) / 60) * 100}%`,
                        height: `${
                          ((parseInt(event.endTime.split(':')[0]) * 60 + parseInt(event.endTime.split(':')[1])) -
                            (parseInt(event.startTime.split(':')[0]) * 60 + parseInt(event.startTime.split(':')[1]))) /
                          60 *
                          100
                        }%`,
                        minHeight: '40px',
                      }}
                    >
                      <div className="flex flex-col h-full">
                        <span className="text-[17px] font-medium">{event.title}</span>
                        <div className="flex items-center gap-2 mt-2 text-sm opacity-80">
                          <Clock className="w-4 h-4" />
                          <span>
                            {event.startTime} - {event.endTime}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}

          {/* Current time indicator */}
          {isToday(selectedDate) && (
            <div
              className="absolute left-16 right-0 flex items-center"
              style={{
                top: `${
                  ((new Date().getHours() * 60 + new Date().getMinutes()) /
                    (24 * 60)) *
                  100
                }%`,
              }}
            >
              <div className="w-2 h-2 rounded-full bg-white" />
              <div className="flex-1 h-px bg-white" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
